import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FCCC } from "types";

import fm13 from "@/assets/friendsWithBenefits/fm_13.png";
import fm18 from "@/assets/friendsWithBenefits/fm_18.png";
import fm25 from "@/assets/friendsWithBenefits/fm_25.png";
import numberPorting from "@/assets/friendsWithBenefits/numberPorting.png";
import phoneInHand from "@/assets/friendsWithBenefits/phoneInHand.png";
import DRLogo from "@/assets/icons/DRLogo.svg?react";
import {
  ArrowRight,
  Close,
  Exit,
  Globe,
  PhoneCall,
  SpeedIcon,
} from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";

import slides from "./slides.json";
const imageMap = {
  phoneInHand,
  fm_13: fm13,
  fm_18: fm18,
  fm_25: fm25,
  numberPorting,
};

interface Props {
  onClose: () => void;
  classNameDescription?: string;
  hasScroll?: boolean;
  onCTAClick?: () => void;
}

export const SlideShow: FCCC<Props> = ({
  className,
  classNameDescription,
  onClose,
  onCTAClick,
}) => {
  const { t } = useTranslation(["portal"], {
    keyPrefix: "friendsWithBenefits.welcome.slides",
  });
  const [currentBar, setCurrentBar] = useState(0);
  const [pauseSlide, setPauseSlide] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [pauseFeedback, setPauseFeedback] = useState(false);
  const animationDuration = 10000;

  const slidesData = slides[currentBar];

  const isLastSlide = currentBar === slides.length - 1;

  const iconMap = {
    phone: <PhoneCall className="w-4 text-primary-100" />,
    speed: <SpeedIcon className="w-4 text-primary-100" />,
    exit: <Exit className="w-4 text-primary-100" />,
    globe: <Globe className="mt-1 w-4 text-primary-100" />,
  };

  const slideClickAnimation = () => {
    setPauseFeedback(true);
    setTimeout(() => {
      setPauseFeedback(false);
    }, 100);
  };

  useEffect(() => {
    let animationFrame: number;
    let startTime = Date.now() - animationProgress * animationDuration;

    const updateProgress = () => {
      const currentTime = Date.now();
      if (!pauseSlide) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        setAnimationProgress(progress);

        if (progress >= 1) {
          // Move to next slide when animation completes
          setCurrentBar((prev) => (prev < slides.length - 1 ? prev + 1 : prev));
          startTime = currentTime; // Reset for next slide
          setAnimationProgress(0);
        }
      } else {
        // When paused, update the startTime to preserve progress
        startTime = currentTime - animationProgress * animationDuration;
      }

      animationFrame = requestAnimationFrame(updateProgress);
    };

    animationFrame = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [currentBar, pauseSlide, animationDuration, animationProgress]);

  return (
    <Transition.Root as={Fragment} appear show>
      <Dialog
        className="relative z-30"
        as="div"
        onClose={onClose}
        data-testid="modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-10 bg-primary-100 opacity-25 transition-opacity" />
        </Transition.Child>
        <div id="modal" className="fixed inset-0 z-40 overflow-y-auto py-2">
          <div className="flex min-h-screen items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "relative mx-6 h-[850px] w-[328px] overflow-hidden rounded-xl bg-white",
                  className,
                )}
              >
                <button
                  className="modal-close-button absolute right-4 top-8 z-10"
                  onClick={onClose}
                >
                  <Close
                    className={`w-6 cursor-auto ${isLastSlide ? "text-black" : "text-white"}`}
                  />
                </button>
                <div className="absolute left-0 right-0 top-2 z-10 px-4">
                  <div className="flex w-full gap-1.5">
                    {[...Array(slides.length)].map((_, index) => (
                      <div
                        key={index}
                        className="h-1.5 flex-1 cursor-pointer overflow-hidden bg-primary-25"
                        onClick={() => {
                          setCurrentBar(index);
                          setAnimationProgress(0);
                          setPauseSlide(false);
                        }}
                      >
                        <div
                          className="h-full bg-primary-100"
                          style={{
                            width:
                              index < currentBar
                                ? "100%"
                                : index === currentBar
                                  ? `${animationProgress * 100}%`
                                  : "0%",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {!isLastSlide ? (
                  <div
                    className={clsx("relative", pauseFeedback && "opacity-50")}
                    onClick={() => {
                      slideClickAnimation();
                      setPauseSlide(!pauseSlide);
                    }}
                  >
                    <div
                      className="h-[220px] bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${imageMap[slidesData.headerImage as keyof typeof imageMap]})`,
                      }}
                    ></div>
                    <div className="flex flex-row justify-between"></div>
                    <Dialog.Title className="p-4 text-xl">
                      {t(`slide${currentBar + 1}.title` as any)}
                    </Dialog.Title>

                    <Dialog.Description
                      className={clsx(classNameDescription, "px-4")}
                      as="div"
                    >
                      <div className="flex flex-col gap-2 text-base font-semibold">
                        <div className="flex flex-row items-start gap-2">
                          <div className="w-4 flex-shrink-0">
                            {iconMap[slidesData.icon1 as keyof typeof iconMap]}
                          </div>
                          <span>
                            {t(`slide${currentBar + 1}.bullet1` as any)}
                          </span>
                        </div>
                        {slidesData.icon2 && (
                          <div className="flex flex-row items-start gap-2">
                            <div className="w-4 flex-shrink-0">
                              {
                                iconMap[
                                  slidesData.icon2 as keyof typeof iconMap
                                ]
                              }
                            </div>
                            <span>
                              {t(`slide${currentBar + 1}.bullet2` as any)}
                            </span>
                          </div>
                        )}

                        {slidesData.icon3 && (
                          <div className="flex flex-row items-start gap-2">
                            <div className="w-4 flex-shrink-0">
                              {
                                iconMap[
                                  slidesData.icon3 as keyof typeof iconMap
                                ]
                              }
                            </div>
                            <span>
                              {t(`slide${currentBar + 1}.bullet3` as any)}
                            </span>
                          </div>
                        )}

                        <div className="my-2 bg-primary-100 p-2 text-base font-normal text-white">
                          {t(`slide${currentBar + 1}.blueBox` as any)}
                        </div>

                        <div className="font-normal">
                          <div className="mb-2 font-semibold">
                            {t("aboutMe")}
                          </div>
                          {t(`slide${currentBar + 1}.description` as any)}
                        </div>
                      </div>
                    </Dialog.Description>
                  </div>
                ) : (
                  <div
                    className={clsx(
                      "flex flex-col gap-20 px-6",
                      pauseFeedback && "opacity-50",
                    )}
                    onClick={() => {
                      slideClickAnimation();
                      setPauseSlide(!pauseSlide);
                    }}
                  >
                    <DRLogo className="static -ml-[10px] h-auto w-[100px] pt-8" />
                    <div className="flex flex-col gap-2 text-center">
                      <h2 className="font-semibold text-primary-100">
                        {t("lastSlide.title")}
                      </h2>
                      <h3 className="font-semibold text-primary-100">
                        {t("lastSlide.subtitle")}
                      </h3>
                      <div className="my-12 whitespace-pre-line text-left">
                        <Trans
                          t={t}
                          i18nKey={"lastSlide.description" as any}
                          components={{
                            br: <br />,
                          }}
                        />
                      </div>

                      <Button onClick={onCTAClick} className="accent">
                        {t("lastSlide.cta")}
                      </Button>
                      <h3 className="mt-8 font-semibold text-primary-100">
                        {t("lastSlide.slogan")}
                      </h3>
                    </div>
                  </div>
                )}
                <div className="flex justify-between p-4">
                  <button
                    onClick={() => {
                      if (currentBar > 0) {
                        setCurrentBar(currentBar - 1);
                        setAnimationProgress(0);
                        setPauseSlide(false);
                      }
                    }}
                  >
                    {slides[currentBar - 1] && (
                      <ArrowRight className="mt-1 h-6 w-6 rotate-180 stroke-primary-100 text-primary-100" />
                    )}
                  </button>
                  <button
                    className="mt-0.5"
                    onClick={() => {
                      if (currentBar < slides.length - 1) {
                        setCurrentBar(currentBar + 1);
                        setAnimationProgress(0);
                        setPauseSlide(false);
                      }
                    }}
                  >
                    {slides[currentBar + 1] && (
                      <ArrowRight className="h-6 w-6 stroke-primary-100 text-blue-500" />
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
